import React, { useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch, bindActionCreators } from 'redux';
import HomeVisitRecordForm from '../../components/Records/HomeVisit/HomeVisitRecordForm';
import * as RecordActions from '../../actions/Records/actions';
import * as UserActions from '../../actions/User/actions';
import { RootState } from '../../store/configureStore';
import Loading from '../../components/common/Loading';
import { HomeVisitRecord } from '../../constants/Records';

interface RouteParams {
  institutionId: string;
  userId: string;
  sequence: string;
}

interface StateProps {
  institution: any;
  userListData: any[];
  homevisit: any;
}

interface DispatchProps {
  recordActions: typeof RecordActions;
  userActions: typeof UserActions;
}

type Props = StateProps & DispatchProps;

const HomeVisitRecordEditContainer: React.FC<Props> = ({
  institution,
  userListData,
  homevisit,
  recordActions,
  userActions,
}) => {
  const { institutionId, userId, sequence } = useParams<RouteParams>();
  const initializedRef = useRef(false);
  const userFetchedRef = useRef(false);

  const user = userListData.find((u: any) => u.user_id === userId);
  const uniqueId = user?.unique_id;

  useEffect(() => {
    if (userListData.length === 0 && !userFetchedRef.current && institutionId) {
      userFetchedRef.current = true;
      userActions.getUsersWithUniqueId(institutionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionId, userListData.length]);

  useEffect(() => {
    if (uniqueId && !initializedRef.current) {
      initializedRef.current = true;
      recordActions.fetchHomeVisitRecord(uniqueId, parseInt(sequence, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueId, sequence]);

  const handleFetchRecord = useCallback(
    (id: string, seq: number) => {
      recordActions.fetchHomeVisitRecord(id, seq);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleUpdate = useCallback(
    (id: string, seq: number, data: Partial<HomeVisitRecord>) => {
      recordActions.updateHomeVisitRecord(id, seq, data);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleResetFlags = useCallback(() => {
    recordActions.resetHomeVisitFlags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) {
    return <Loading />;
  }

  return (
    <HomeVisitRecordForm
      institutionId={institutionId}
      institutionName={institution?.institution_name || ''}
      userId={userId}
      userName={user.user_name}
      uniqueId={user.unique_id || ''}
      currentRecord={homevisit.current}
      isEdit={true}
      executing={homevisit.executing}
      created={homevisit.created}
      updated={homevisit.updated}
      onFetchRecord={handleFetchRecord}
      onCreate={() => {}}
      onUpdate={handleUpdate}
      onResetFlags={handleResetFlags}
    />
  );
};

function mapStateToProps(state: RootState): StateProps {
  return {
    institution: state.institution.institution,
    userListData: state.user.listData,
    homevisit: state.records.homevisit,
  };
}

function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    recordActions: bindActionCreators(RecordActions, dispatch),
    userActions: bindActionCreators(UserActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(HomeVisitRecordEditContainer);
